/* Occasions behind /genieten-vieren.
 *
 * Data only, deliberately: the page pairs each entry with a line icon of its
 * own, and the navbar search pulls the same labels, images and keywords from
 * here. Keeping the icons out means importing this into the navbar does not
 * drag eight SVG components into every page's bundle.
 */

export type OccasionId =
  | 'bruiloft' | 'verjaardag' | 'geboorte' | 'jubileum'
  | 'geslaagd' | 'cadeau' | 'bedrijfsfeest' | 'feestdagen'

/** Een foto in het sfeergalerij-paneel. Los van de catalogus, want een
 *  bruidstaart hangt niet aan één product uit de winkel — hij wordt op maat
 *  gemaakt. De foto's zijn dus voorbeelden, geen bestelbare artikelen. */
export interface OccasionImage {
  src:     string
  alt:     string
  /** Kort bijschrift over de foto, zodat duidelijk is dat het een voorbeeld is. */
  caption: string
  /** Is de foto wél een artikel uit de winkel, dan staat hier de productpagina
   *  en verschijnt onder de foto een knop om hem te bekijken en te bestellen. */
  href?:   string
  /** Naam van dat artikel, voor op die knop. */
  label?:  string
}

/** Een uitgelichte ontwerp-pagina in het paneel, als grote knop. */
export interface OccasionAction {
  label:       string
  description: string
  href:        string
  /** Met een foto wordt de knop een fotokaart (Voortmans Ontbijt). */
  image?:      string
  imageAlt?:   string
}

/** De offerte-aanvraag onder de galerij: alleen bij gelegenheden die altijd
 *  op maat gaan (bruiloft). */
export interface OccasionQuote {
  label:    string
  sublabel: string
  href:     string
  /** Korte uitleg bij de knop, als opvallend kadertje. */
  note:     string
}

/** Wat er in de rechterkolom van het detailpaneel staat:
 *  - `products` — suggesties uit de winkel (de standaard)
 *  - `gallery`  — voorbeeldfoto's, want dit gaat altijd op maat
 *  - `actions`  — knoppen naar de ontwerp-pagina's (foto- of teksttaart) */
export type OccasionPanel = 'products' | 'gallery' | 'actions'

export interface Occasion {
  id:           OccasionId
  label:        string
  /** Alt text for both the card image and the large detail image. */
  alt:          string
  image:        string
  detailImage:  string
  description:  string
  benefits:     string[]
  /** Wat het detailpaneel toont. */
  panel:        OccasionPanel
  /** Slugs resolved against the real catalogue through /api/products/[slug].
   *  Alleen gebruikt bij `panel: 'products'`. */
  productSlugs: string[]
  /** Alleen bij `panel: 'gallery'`. */
  gallery?:     OccasionImage[]
  /** Kopje boven de galerij; standaard "Zo kan het eruitzien". */
  galleryTitle?: string
  /** Alleen bij `panel: 'gallery'`: de offerteknop met uitleg eronder. */
  quote?:       OccasionQuote
  /** Bij `panel: 'actions'` het hele paneel; bij `panel: 'products'`
   *  optioneel een paar knoppen boven de suggesties (ontwerp-pagina's, pakket). */
  actions?:     OccasionAction[]
  /** Existing shop listing this occasion belongs to ("Bekijk alles"). */
  browseHref:   string
  /** Existing configurator pages, kept so their links don't disappear. */
  configLinks:  { label: string; href: string }[]
  /** Extra terms the site search should match on, beyond the label itself. */
  keywords:     string[]
}

export const OCCASIONS: Occasion[] = [
  {
    id: 'bruiloft',
    keywords: ['bruiloft', 'bruidstaart', 'trouwen', 'trouwerij', 'huwelijk', 'bruid', 'bruidspaar', 'wedding'],
    label: 'Bruiloft',
    alt: 'Bruidstaart met marsepeinen rozen van Bakkerij Voortman',
    image: '/images/gelegenheden/bruiloft-kaart.webp',
    detailImage: '/images/gelegenheden/bruiloft.webp',
    description:
      'Een bijzondere dag verdient iets bijzonders. Van elegante bruidstaarten tot feestelijk gebak voor jullie gasten: ontdek onze mogelijkheden voor een onvergetelijke bruiloft.',
    benefits: [
      'Persoonlijk afgestemd op jullie wensen',
      'Van bruidstaart tot klein gebak',
      'Vers bereid voor jullie grote dag',
    ],
    panel: 'gallery',
    productSlugs: [],
    gallery: [
      {
        src: '/images/gelegenheden/bruiloft.webp',
        alt: 'Bruidstaart met roze marsepeinen rozen en parels',
        caption: 'Marsepeinen rozen, met de hand gemaakt',
      },
      {
        src: '/images/gelegenheden/bruiloft-2.webp',
        alt: 'Bruidstaart op etagère met roze rozen',
        caption: 'Op etagère, met roze rozen',
      },
      {
        src: '/images/gelegenheden/bruiloft-6.webp',
        alt: 'Drielaagse bruidstaart met vers fruit en bloemen',
        caption: 'Drie lagen met vers fruit en bloemen',
      },
      {
        src: '/images/gelegenheden/bruiloft-5.webp',
        alt: 'Bruidstaart met rood fruit op de feesttafel',
        caption: 'Met rood fruit, klaar voor het aansnijden',
      },
      {
        src: '/images/gelegenheden/bruiloft-3.webp',
        alt: 'Strakke witte bruidstaart met drie lagen',
        caption: 'Strak en wit, drie lagen',
      },
      {
        src: '/images/gelegenheden/bruiloft-4.webp',
        alt: 'Bruidstaart met chocolade en rood fruit',
        caption: 'Met chocolade en rood fruit',
      },
    ],
    /* Een bruidstaart gaat altijd via een bericht: de bakkerij wil de wensen
       op papier hebben (smaak, formaat, datum) voordat er een prijs komt.
       Daarom hier bewust geen telefoonnummer. De link springt op de
       contactpagina meteen naar het formulier, met het onderwerp al gekozen. */
    quote: {
      label:    'Vraag een offerte aan',
      sublabel: 'voor jullie bruidstaart',
      href:     '/contact?onderwerp=Bruidstaart#bericht',
      note:     'Elke bruidstaart maken we op maat. Stuur ons een bericht met de datum, het aantal gasten en wat jullie voor ogen hebben, dan maken we een offerte.',
    },
    browseHref: '/winkel?category=taart',
    configLinks: [],
  },
  {
    id: 'verjaardag',
    keywords: ['verjaardag', 'verjaardagstaart', 'jarig', 'jarige', 'birthday', 'feestje', 'kaarsjes'],
    label: 'Verjaardag',
    alt: 'Feestelijk gedekte tafel in de tuin met ballonnen en slingers',
    image: '/images/gelegenheden/verjaardag-kaart.webp',
    detailImage: '/images/gelegenheden/verjaardag.webp',
    description:
      'Maak van iedere verjaardag een bijzonder moment met een feestelijke verjaardagstaart van Bakkerij Voortman. Van een elegante klassieker tot een persoonlijke creatie met foto, tekst of een ontwerp naar wens. Voor iedere verjaardag maken wij iets moois én vooral iets lekkers. Met liefde voor het ambacht en oog voor detail bereiden wij iedere taart vers. Zo zorgen we ervoor dat uw taart niet alleen prachtig op tafel staat, maar ook heerlijk smaakt.',
    benefits: [
      'Met zorg en vakmanschap bereid',
      'Persoonlijke taarten, passend bij iedere gelegenheid',
      'Een ruime keuze voor zowel kleine als grote verjaardagen',
    ],
    panel: 'products',
    productSlugs: ['verjaardagstaart-jongen', 'verjaardagstaart-meisje'],
    actions: [
      {
        label: 'Fotologotaart',
        description: 'Zet je eigen foto of logo als eetbare print op een slagroomtaart.',
        href: '/genieten-vieren/fototaart',
      },
      {
        label: 'Taart met tekst',
        description: 'Laat de bakker jouw felicitatie met chocolade op de taart schrijven.',
        href: '/genieten-vieren/slagroomtaart',
      },
    ],
    browseHref: '/winkel?category=taart',
    configLinks: [],
  },
  {
    id: 'geboorte',
    keywords: ['geboorte', 'kraamvisite', 'kraamfeest', 'baby', 'beschuit met muisjes', 'muisjes', 'pasgeboren', 'zwanger', 'krentenwegge'],
    label: 'Geboorte',
    alt: 'Wit rompertje met houten speeltjes in een mandje',
    image: '/images/gelegenheden/geboorte-kaart.webp',
    detailImage: '/images/gelegenheden/geboorte.webp',
    description:
      'Een kleintje geboren? Een bijzonder moment dat vraagt om iets feestelijks. Vier de geboorte met onze heerlijke lekkernijen, speciaal om uit te delen aan familie, vrienden, collega’s en andere dierbaren.',
    benefits: [
      'Feestelijk en persoonlijk',
      'Mooi om uit te delen',
      'Vers en met zorg bereid in onze bakkerij',
    ],
    /* Grote foto's in plaats van een lijstje: de taarten zijn het verhaal.
       Elke foto is wél een artikel uit de winkel, dus de knop eronder gaat
       naar de productpagina. */
    panel: 'gallery',
    galleryTitle: 'Onze suggesties',
    productSlugs: [],
    gallery: [
      {
        src: '/images/producten/geboortetaart-jongen.webp',
        alt: 'Geboortetaart voor een jongen, met naam en geboortedatum',
        caption: 'Geboortetaart jongen, met naam en geboortedatum',
        href: '/winkel/product/geboortetaart-jongen',
        label: 'Geboortetaart jongen',
      },
      {
        src: '/images/producten/geboortetaart-meisje.webp',
        alt: 'Geboortetaart voor een meisje, met naam en geboortedatum',
        caption: 'Geboortetaart meisje, met naam en geboortedatum',
        href: '/winkel/product/geboortetaart-meisje',
        label: 'Geboortetaart meisje',
      },
      {
        src: '/images/producten/krentenwegge-met-naam.webp',
        alt: 'Twentse krentenwegge met de naam op marsepein',
        caption: 'Krentenwegge met naam, voor de kraamvisite',
        href: '/winkel/product/krentenwegge-met-naam',
        label: 'Krentenwegge met naam',
      },
      {
        src: '/images/producten/verjaardagstaart-jongen.webp',
        alt: 'Verjaardagstaart voor een jongen',
        caption: 'Verjaardagstaart jongen',
        href: '/winkel/product/verjaardagstaart-jongen',
        label: 'Verjaardagstaart jongen',
      },
      {
        src: '/images/producten/verjaardagstaart-meisje.webp',
        alt: 'Verjaardagstaart voor een meisje',
        caption: 'Verjaardagstaart meisje',
        href: '/winkel/product/verjaardagstaart-meisje',
        label: 'Verjaardagstaart meisje',
      },
    ],
    browseHref: '/winkel?category=geboorte',
    configLinks: [],
  },
  {
    id: 'jubileum',
    keywords: ['jubileum', 'jubilaris', 'mijlpaal', 'huwelijksjubileum', 'dienstjaren', 'anniversary'],
    label: 'Jubileum',
    alt: 'Proosten met champagneglazen op een jubileum',
    image: '/images/gelegenheden/jubileum-kaart.webp',
    detailImage: '/images/gelegenheden/jubileum.webp',
    description:
      'Een bijzondere mijlpaal verdient een feestelijke viering. Of het nu gaat om een huwelijksjubileum, een bedrijfsjubileum of een andere bijzondere gelegenheid: met onze feestelijke lekkernijen maken we van ieder jubileum een moment om samen van te genieten. Van klassiek en stijlvol tot persoonlijk en geheel naar wens: wij zorgen met zorg en vakmanschap voor iets lekkers dat past bij de gelegenheid.',
    benefits: [
      'Voor kleine én grote mijlpalen',
      'Persoonlijke mogelijkheden naar wens',
      'Feestelijk en met zorg verzorgd',
    ],
    panel: 'products',
    productSlugs: [
      'petit-four-met-logo',
      'petit-glace-gesorteerd',
      'gebakjes-gesorteerd',
    ],
    browseHref: '/winkel?category=gebakjes',
    configLinks: [],
  },
  {
    id: 'geslaagd',
    keywords: ['geslaagd', 'afstuderen', 'diploma', 'examen', 'diploma-uitreiking', 'afgestudeerd', 'geslaagdtaart'],
    label: 'Geslaagd',
    alt: 'Afstudeerhoed met diploma',
    image: '/images/gelegenheden/geslaagd-kaart.webp',
    detailImage: '/images/gelegenheden/geslaagd.webp',
    description:
      'Geslaagd! Dat verdient natuurlijk een feestje. Vier het succes met een heerlijke taart, gebak of een persoonlijke traktatie.',
    benefits: [
      'Perfect voor een geslaagd feestje',
      'Taarten met foto of tekst',
      'Vers en feestelijk',
    ],
    panel: 'actions',
    productSlugs: [],
    actions: [
      {
        label: 'Slagroomtaart met tekst',
        description: '‘Gefeliciteerd met je diploma!’ De bakker schrijft jouw tekst met chocolade op de taart.',
        href: '/genieten-vieren/slagroomtaart',
      },
      {
        label: 'Slagroomtaart met foto',
        description: 'Liever de geslaagde zelf op de taart? Zet een foto op een verse slagroomtaart.',
        href: '/genieten-vieren/fototaart',
      },
    ],
    browseHref: '/winkel?category=taart',
    configLinks: [],
  },
  {
    id: 'cadeau',
    keywords: ['cadeau', 'kado', 'relatiegeschenk', 'geschenk', 'presentje', 'bedankje', 'kerstpakket', 'cadeaubon', 'waardebon', 'ontbijt'],
    label: 'Cadeau & Relatiegeschenk',
    alt: 'Ingepakt cadeau met touw op een houten tafel',
    image: '/images/gelegenheden/cadeau-kaart.webp',
    detailImage: '/images/gelegenheden/cadeau.webp',
    description:
      'Een smakelijk cadeau om iemand mee te verrassen of een bijzondere gelegenheid mee te vieren. Met aandacht samengesteld en feestelijk verzorgd, zodat ieder cadeau een persoonlijke en smaakvolle indruk achterlaat.',
    benefits: [
      'Een smakelijk cadeau voor iedere gelegenheid',
      'Persoonlijk samengesteld en feestelijk verzorgd',
    ],
    panel: 'products',
    productSlugs: ['waardebon', 'cadeaupakket'],
    actions: [
      {
        label: 'Voortmans Ontbijt',
        description: 'Een compleet ontbijtpakket om cadeau te geven, met vers brood en lekkers uit de bakkerij.',
        href: '/winkel/pakket/voortmans-ontbijt',
        image: '/images/gelegenheden/voortmans-ontbijt.webp',
        imageAlt: 'Ontbijtpakket met croissants, brood, jam en sap in een doos',
      },
    ],
    browseHref: '/winkel?category=cadeau',
    configLinks: [],
  },
  {
    id: 'bedrijfsfeest',
    keywords: ['bedrijfsfeest', 'traktatie', 'trakteren', 'kantoor', 'zakelijk', 'borrel', 'personeelsfeest', 'vergadering', 'team'],
    label: 'Bedrijfsfeest & Traktatie',
    alt: 'Schaal met verse soesjes met poedersuiker',
    image: '/images/gelegenheden/bedrijfsfeest-kaart.webp',
    detailImage: '/images/gelegenheden/bedrijfsfeest.webp',
    description:
      'Een verjaardag op het werk, een jubileum of gewoon iets te vieren? Met onze traktaties en lekkernijen maak je ieder moment op kantoor extra gezellig.',
    benefits: [
      'Geschikt voor kleine en grote groepen',
      'Handig om uit te delen',
      'Ook voor zakelijke bestellingen',
    ],
    panel: 'products',
    productSlugs: [
      'saucijzenbroodje',
      'frikandelbroodje',
      'kipsate-broodje',
      'soesje',
      'gesorteerde-koffiebroodjes',
      'petit-glace-gesorteerd',
      'gebakjes-gesorteerd',
    ],
    browseHref: '/winkel?category=hartige-broodjes',
    configLinks: [],
  },
  {
    id: 'feestdagen',
    keywords: ['feestdagen', 'kerst', 'kerstmis', 'nieuwjaar', 'oud en nieuw', 'sinterklaas', 'pasen', 'feestdag', 'speculaas'],
    label: 'Feestdagen',
    alt: 'Kop koffie op een stapel warme truien met herfstbladeren',
    image: '/images/gelegenheden/feestdagen-kaart.webp',
    detailImage: '/images/gelegenheden/feestdagen.webp',
    description:
      'Maak de feestdagen extra bijzonder met iets lekkers van Bakkerij Voortman. Van gezellige traktaties tot feestelijk gebak: voor ieder moment iets passends.',
    benefits: [
      'Voor iedere feestelijke gelegenheid',
      'Ambachtelijk bereid',
      'Feestelijk voor thuis én op het werk',
    ],
    panel: 'products',
    productSlugs: [
      'krentenwegge-4-sneetjes',
      'suikerbrood',
      'kruidcake',
      'aardbei-vlaai',
      'rozijnenwegge-4-sneetjes',
      'krentenbrood',
      'gebakjes-gesorteerd',
      'bonbons',
    ],
    browseHref: '/winkel?category=gevuld-brood',
    configLinks: [],
  },
]

/** Selected when /genieten-vieren opens without ?gelegenheid=. */
export const DEFAULT_OCCASION: OccasionId = 'verjaardag'

/** Deep link that opens the page with this occasion already selected. The
 *  hash lands the visitor on the card grid rather than the top of the hero. */
export function occasionHref(id: OccasionId): string {
  return `/genieten-vieren?gelegenheid=${id}#gelegenheden`
}

export function isOccasionId(value: string | null | undefined): value is OccasionId {
  return !!value && OCCASIONS.some((o) => o.id === value)
}
